import { makeAccess } from '../config/factory-controller'
// import { ErrorMappingService } from './error-mapping.service'

// Tipos locais (não exportados para evitar conflitos)
interface JourneyResponse {
  journeyId: number
  userId: number
  company: string
  routeId: number
  totalAmount: number
  closingTime: string | null
  status: string
  creationDate: string
  updateDate: string
  companyName?: string
  userName?: string
}

interface PaginationInfo {
  currentPage: number
  pageSize: number
  totalItems: number
  totalPages: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

interface PagedResponse<T> {
  data: T[]
  pagination: PaginationInfo
}

// Service simplificado
export const journeyService = {
  async getPreviousClosings(): Promise<JourneyResponse[]> {
    try {
      const API_URL =
        import.meta.env.VITE_REACT_APP_API_URL || 'https://localhost:50962'
      const cleanApiUrl = API_URL.replace(/\/+$/, '')
      const url = `${cleanApiUrl}/journey?status=1`

      // Headers centralizados
      const { key: applicationKey, token: applicationToken } =
        await makeAccess()
      const apiVersion = Number(import.meta.env.VITE_REACT_APP_API_VERSION)

      if (!applicationKey || !applicationToken) {
        throw new Error('Credenciales de aplicación faltantes (key/token).')
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'x-api-version': String(apiVersion),
        'x-application-key': applicationKey,
        'x-application-token': applicationToken,
      }

      // Busca o token do localStorage
      const token = localStorage.getItem('token')

      // Adiciona o token de autorização se existir
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()

      return result.data || []
    } catch (error) {
      // Re-lança o erro para que o Redux possa tratá-lo
      throw error
    }
  },

  async getPreviousClosingsPaged(
    page: number = 1,
    pageSize: number = 10,
    company?: string,
    status?: number | undefined,
    startDate?: string,
    endDate?: string,
  ): Promise<PagedResponse<JourneyResponse>> {
    try {
      const API_URL =
        import.meta.env.VITE_REACT_APP_API_URL || 'https://localhost:50962'
      const cleanApiUrl = API_URL.replace(/\/+$/, '')

      // Construir parâmetros da URL com validação
      const params = new URLSearchParams()

      // Adicionar status (incluindo string vazia para "Todos los estados")
      if (status !== undefined && status !== null)
        params.append('status', status.toString())

      // Validar e adicionar company
      if (company && company.trim() !== '') {
        params.append('company', company.trim())
      }

      // Datas (nome dos parâmetros pode ser ajustado conforme API real)
      if (startDate) {
        params.append('startDate', startDate)
      }
      if (endDate) {
        params.append('endDate', endDate)
      }

      // Validar e adicionar paginação
      const validPage = Math.max(1, page)
      const validPageSize = Math.min(100, Math.max(1, pageSize))
      params.append('page', validPage.toString())
      params.append('pageSize', validPageSize.toString())

      const url = `${cleanApiUrl}/journey?${params.toString()}`

      // Headers centralizados
      const { key: applicationKey, token: applicationToken } =
        await makeAccess()
      const apiVersion = Number(import.meta.env.VITE_REACT_APP_API_VERSION)

      if (!applicationKey || !applicationToken) {
        throw new Error('Credenciales de aplicación faltantes (key/token).')
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'x-api-version': String(apiVersion),
        'x-application-key': applicationKey,
        'x-application-token': applicationToken,
      }

      // Busca o token do localStorage
      const token = localStorage.getItem('token')

      // Adiciona o token de autorização se existir
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        try {
          const errorResult = await response.json()
          const errorMessage =
            errorResult.message ||
            `Error ${response.status}: ${response.statusText}`
          throw new Error(errorMessage)
        } catch (parseError) {
          throw new Error(`Error ${response.status}: ${response.statusText}`)
        }
      }

      const result = await response.json()

      const apiData = result?.data || {}
      const items: JourneyResponse[] = (apiData.data || []).map((it: any) => ({
        journeyId: it.journeyId,
        userId: it.userId,
        company: it.company,
        routeId: it.routeId,
        totalAmount: it.totalAmount,
        closingTime: it.closingTime,
        status: it.status,
        creationDate: it.creationDate,
        updateDate: it.updateDate,
        companyName: it.companyName || it.CompanyName,
        userName: it.userName || it.UserName,
      }))

      const rawPagination = apiData.pagination || {}
      const totalItems = rawPagination.totalItems ?? items.length
      const totalPages = rawPagination.totalPages ?? (totalItems > 0 ? 1 : 0)

      const pagination: PaginationInfo = {
        currentPage: rawPagination.currentPage || page,
        pageSize: rawPagination.pageSize || pageSize,
        totalItems,
        totalPages,
        hasPreviousPage: rawPagination.hasPreviousPage || false,
        hasNextPage: rawPagination.hasNextPage || false,
      }

      return { data: items, pagination }
    } catch (error) {
      throw error
    }
  },
}
